import { observer } from 'mobx-react-lite'
import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'
import Button from '../Button'
import FormComboAsyncV2 from '../form/FormComboAsyncV2'
import FormDatePicker from '../form/FormDatePicker'
import FormInput from '../form/FormInput'
import { FormMarkDownEditor } from '../form/FormMarkDownEditor'
import { SmartForm } from '../form/SmartForm'
import NameIcon from '../shared/NameIcon'
import Spinner from '../Spinner'
import type { ContactJson, TaskJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { assigneesFetcher } from 'app/lib/fetchers/assigneesFetcher'
import { ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import { RoleOptionLabel } from 'app/components/tasks/TaskAssigneeLabel'
import { Group } from 'iconoir-react'
import { createContactOption } from 'app/components/contacts/contactOption'
import { useEditTaskModal } from './useEditTaskModal'

const editSchema = z.object({
  id: z.string().min(1, { message: `A task id is required` }),
  title: z.string(),
  dueDate: z.string().min(1, { message: `A due date is required` }),
  status: z.enum([`open`, `closed`]).default(`open`),
  ownerId: z
    .string()
    .or(z.object({ id: z.string(), name: z.string(), value: z.string() }).transform((owner) => owner.id))
    .optional(),
  contactId: z
    .string()
    .or(z.object({ id: z.string(), name: z.string(), value: z.string() }).transform((contact) => contact.id))
    .optional(),
  description: z.string().optional(),
})

const editValidator = withZod(editSchema)

/**
 * Full task form for editing an existing task. Creating a task goes through
 * QuickCreateTaskModal (single and bulk).
 */
// observer: reads taskStore.saving and the location store through useEditTaskModal.
export const EditTaskModal = observer(function EditTaskModal({
  onTaskCreatedOrUpdated,
  editingTask,
  onTaskDelete,
  defaultContact,
}: {
  onTaskCreatedOrUpdated: (task?: TaskJson) => void
  editingTask: TaskJson
  onTaskDelete?: (taskId: string) => Promise<void>
  defaultContact?: ContactJson
}) {
  const {
    workspaceId,
    locations,
    isLoading,
    isSaving,
    isDeleting,
    selectedContact,
    selectedOwner,
    setSelectedOwner,
    searchContacts,
    handleContactChange,
    submitTask,
    deleteTask,
  } = useEditTaskModal({ editingTask, defaultContact, onTaskCreatedOrUpdated, onTaskDelete })

  return (
    <div className="flex w-full flex-col items-center justify-center md:w-[670px]">
      {isLoading ? (
        <div className={`flex h-full items-center justify-center p-12`}>
          <Spinner size={20} />
        </div>
      ) : (
        <div className="w-full">
          <SmartForm
            className="px-6"
            onCancel={() => closeModal()}
            validator={editValidator}
            hiddenInputs={[{ name: `id`, value: editingTask.id }]}
            reloadDocument={false}
            preventSubmit={true}
            onSubmit={(data) => submitTask(data)}
            hideButtons
          >
            <FormInput
              name="title"
              label="Title"
              placeholder="Enter a title..."
              required={true}
              labelClassName="text-body-sm text-strong"
              defaultValue={editingTask.title}
              cols={6}
            />
            <FormMarkDownEditor
              name="description"
              label="Description"
              placeholder="Add a description..."
              cols={6}
              labelClassName="text-body-sm text-strong"
              description={editingTask.description}
            />
            <FormComboAsyncV2
              cols={2}
              name="contactId"
              disabled={!!defaultContact}
              showSearchInDropdown={true}
              label="Contact"
              value={selectedContact}
              optionsMapper={(res) => res?.hits?.map((hit: ContactJson) => createContactOption(hit, locations))}
              fetcher={({ query, workspaceId: ws }) => searchContacts({ query, workspaceId: ws })}
              customIcon={
                selectedContact?.name && (
                  <NameIcon fullName={selectedContact.name[0]} heightClassName={`h-5`} widthClassName={`w-5`} isTiny />
                )
              }
              additionalParams={workspaceId ? { workspaceId } : {}}
              labelClassName="text-body-sm text-strong"
              inputClassName="!text-sm !font-normal !leading-5 !text-default"
              onChange={(val) => handleContactChange(val)}
              newLineError={true}
              useSingleFieldInput
              showErrorIcon
            />
            <FormComboAsyncV2
              name="ownerId"
              label="Task Owner"
              key={selectedContact?.meta?.locationId}
              // `locationId` arrives through additionalParams; the `key` above remounts the
              // combo when the contact's location changes so the roster is re-read for it.
              fetcher={({ query, locationId }) => assigneesFetcher({ query, locationId, workspaceId })}
              inputClassName="!text-sm !font-normal !leading-5 !text-default"
              labelClassName="text-body-sm text-strong"
              customIcon={
                selectedOwner?.id.startsWith(ROLE_ASSIGNEE_PREFIX) ? (
                  <Group className="h-5 w-5 text-subtle" strokeWidth={1.75} />
                ) : (
                  selectedOwner?.name && (
                    <NameIcon
                      fullName={selectedOwner.name[0]}
                      heightClassName={`h-5`}
                      widthClassName={`w-5`}
                      borderClassName="border border-strong"
                      backgroundClassName="bg-surface-strong"
                      textClassName="text-strong font-bold"
                      isTiny
                    />
                  )
                )
              }
              value={selectedOwner}
              additionalParams={
                selectedContact?.meta?.locationId
                  ? { locationId: selectedContact.meta.locationId }
                  : { workspaceId: workspaceId || `` }
              }
              optionsMapper={(options) => {
                if (options.total > 0) {
                  return options.hits.map((option) => ({
                    id: option.id,
                    name: option.name ?? option.email ?? `Unknown`,
                    value: option.id,
                    meta:
                      option.kind === `role`
                        ? {
                            isDisabled: option.disabled,
                            customComboOption: <RoleOptionLabel name={option.name} disabled={option.disabled} />,
                          }
                        : {
                            nameInitial: true,
                            textClassName: `text-strong font-bold`,
                            backgroundClassName: `bg-surface-strong`,
                            borderClassName: `border border-strong`,
                            locationAccess: option,
                          },
                  }))
                }
                return []
              }}
              onChange={(value) => setSelectedOwner(value)}
              cols={2}
              newLineError={true}
              showErrorIcon
              showSearchInDropdown={true}
            />
            <FormDatePicker
              labelClassName="text-body-sm text-strong"
              name="dueDate"
              label="Due Date"
              cols={2}
              value={editingTask.dueDate}
              allowFutureDates
              allowPastDates={false}
            />
            <div className="col-span-6 -mx-6 mt-4 flex items-center justify-between border-t border-default px-6 py-3">
              <Button label="Delete" size="md" mode="danger" loading={isDeleting} onClick={deleteTask} />
              <div className="flex items-center gap-2">
                <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
                <Button type="submit" label="Save" mode="primary" loading={isSaving} size="md" />
              </div>
            </div>
          </SmartForm>
        </div>
      )}
    </div>
  )
})
